import React from 'react';
import './Home.css';
import { softwareSections } from '../helpers/Softwarelisting';
import TemplateCard from '../UI/TemplateCard';
import SkeletonCard from '../UI/SkeletonCard';
import { motion } from 'framer-motion';
import ConfigModal from '../UI/ConfigModal';
import { useEnvironmentManagement } from '../hooks/useEnvironmentManagement';
import { useTemplateManagement } from '../hooks/useTemplateManagement';
import { useDispatch } from 'react-redux';
import { showNotification } from '../store/slices/NotificationSystemSlice';
import SearchBar from '../UI/SearchBar';
import GreenBg from '../assets/greenbg.png';
import { subtitles, headings } from '../helpers/enums';
import { useInstanceManagement } from '../hooks/useInstanceManagement';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Silk from '../UI/Silk/Silk';
import CenterTyping from '../UI/CenterTyping/CenterTyping';

const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 60, scale: 0.95 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 500,
      damping: 30,
      mass: 0.7,
    },
  },
};


const Home = () => {
  const dispatch = useDispatch();
  const {
    isTemplatesLoading,
    selectedTemplate,
    templateInputConfigs,
    openGitModal,
    sortedTemplateList,
    handleInstall,
    handleCloseGitModal,
    createdEnvs,
    setCreatedEnvs
  } = useTemplateManagement();

  const {
    instances,
    timeAgo,
    handleDeleteEnv,
    refreshEnvironment,
    handleResume,
    handlePause,
    handleStart,
    fetchInstances,
  } = useInstanceManagement();

  const {
    loadingCards,
    createEnvWithInputs,
    handleCreateEnvironment
  } = useEnvironmentManagement((templateName) => {
    setCreatedEnvs(prev => [...prev, templateName]);
  }, fetchInstances);

  const [searchValue, setSearchValue] = React.useState('');
  // Tag filter state
  const [selectedTag, setSelectedTag] = React.useState('All');

  const allTags = React.useMemo(() => {
    const tags = new Set();
    sortedTemplateList.forEach(t => (t.templateTags || []).forEach(tag => tags.add(tag.toUpperCase())));
    const arr = Array.from(tags);
    return arr;
  }, [sortedTemplateList]);
  const tagList = ['All', ...allTags];

  const getInstanceForTemplate = (templateName) =>
    instances.find((inst) => inst.templateName === templateName);

  // Combined tag and search filter
  const filterSoftware = (templateName, templateTags) => {
    const matchesSearch = templateName.toLowerCase().includes(searchValue.toLowerCase());
    // Convert templateTags to uppercase for UI filtering
    const upperTags = (templateTags || []).map(tag => tag.toUpperCase());
    const matchesTag = selectedTag === 'All' || upperTags.includes(selectedTag);
    return matchesSearch && matchesTag;
  };

  // Calculate all filtered results
  const allFilteredSoftwares = Object.entries(softwareSections)
    .flatMap(([section, keys]) =>
      sortedTemplateList.filter(
        item => keys.includes(item.templateName) && filterSoftware(item.templateName, item.templateTags)
      )
    );
  const hasResults = allFilteredSoftwares.length > 0;

  const handleModalSubmit = (data) => {
    createEnvWithInputs(data);
    handleCloseGitModal();
    dispatch(showNotification({
      message: 'Instance Launched Successfully',
      success: true,
      duration: 3000,
      position: 'top-center',
    }));
  };

  const [subtitle, setSubtitle] = React.useState(subtitles[0]);
  const [heading, setHeading] = React.useState(headings[0]);
  React.useEffect(() => {
    setSubtitle(subtitles[Math.floor(Math.random() * subtitles.length)]);
    setHeading(headings[Math.floor(Math.random() * headings.length)]);
  }, []);

  // Debug: Log instances and their URLs whenever instances change
  React.useEffect(() => {
    console.log('DEBUG: Current instances:', instances);
    instances.forEach(inst => {
      console.log(`DEBUG: Instance ${inst.envName} envExports:`, inst.envExports);
      if (inst.envExports && inst.envExports.url) {
        console.log(`DEBUG: Instance ${inst.envName} URL:`, inst.envExports.url);
      }
    });
  }, [instances]);

  return (
    <div className="main-content">
      <img src={GreenBg} alt="Green Background" className="green-bg" />
      <motion.div
        className="title-wrapper"
        initial={{ opacity: 0, filter: 'blur(8px)' }}
        animate={{ opacity: 1, filter: 'blur(0px)' }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
      >
        <div className="toolkit-title">
          {heading}<br />
        </div>
      </motion.div>
      <CenterTyping text={subtitle} speed={100} />;
      <div className="search-bar-wrapper">
        {isTemplatesLoading ? (
          <>
            <div className="searchbar-skeleton" />
            <div className="searchbar-chips-skeleton">
              {Array.from({ length: 5 }).map((_, i) => (
                <div className="searchbar-chip-skeleton" key={i} />
              ))}
            </div>
          </>
        ) : (
          <SearchBar
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            tags={tagList}
            selectedTag={selectedTag}
            onTagChange={setSelectedTag}
          />
        )}
      </div>

      {isTemplatesLoading ? (
        <div className="software-listing-wrapper">
          {Array.from({ length: 13 }).map((_, index) => (
            <div key={`skeleton-${index}`}>
              <SkeletonCard />
            </div>
          ))}
        </div>
      ) : !hasResults ? (
        <div className="no-results-message">
          No software found matching your search and filters.
        </div>
      ) : (
        Object.entries(softwareSections).map(([section, keys]) => {
          // Filter and get software data for this section
          const sectionSoftwares = sortedTemplateList.filter(
            item => keys.includes(item.templateName) && filterSoftware(item.templateName, item.templateTags)
          );
          if (sectionSoftwares.length === 0) return null;
          return (
            <div key={section} className={`software-section${section === 'Essentials' ? ' essentials-glass' : ''}`} style={{ position: section === 'Essentials' ? 'relative' : undefined }}>
              {section === 'Essentials' && (
                <div style={{ position: 'absolute', inset: 0, zIndex: 0, borderRadius: 20, overflow: 'hidden' }}>
                  <Silk
                    speed={8}
                    scale={1}
                    color="#0A3D4A"
                    noiseIntensity={1.5}
                    rotation={1}
                  />
                </div>
              )}
              <h2 className="software-section-title" style={{ position: 'relative', zIndex: 1 }}>
                {section === 'Essentials' ? <p className='essentials-title'><AutoAwesomeIcon sx={{ color: '#fff' }} /> {section}</p> : <p className='section-border'>{section}</p>}
              </h2>
              <motion.div
                className={`software-cards-row${section === 'Essentials' ? '-essential' : ''}`}
                variants={containerVariants}
                initial="hidden"
                animate="show"
                style={section === 'Essentials' ? { position: 'relative', zIndex: 1 } : {}}
              >
                {sectionSoftwares.map((item, index) => (
                  <motion.div
                    key={item.templateName}
                    variants={cardVariants}
                  >
                    <TemplateCard
                      logo={item.templateLogo}
                      title={item.templateName}
                      description={item.templateDescription}
                      documentationLink={item.documentationLink}
                      templateName={item.templateName}
                      onClick={() => handleInstall(item, createEnvWithInputs)}
                      loadingCards={loadingCards}
                      createdEnvs={createdEnvs}
                      isDevelopment={section === 'Coming Soon'}
                      index={index}
                      isFree={item.isFree}
                      instance={getInstanceForTemplate(item.templateName)}
                      timeAgo={timeAgo}
                      handleDeleteEnv={handleDeleteEnv}
                      refreshEnvironment={refreshEnvironment}
                      handleResume={handleResume}
                      handlePause={handlePause}
                      handleStart={handleStart}
                    />
                  </motion.div>
                ))}
              </motion.div>
            </div>
          );
        })
      )}
      {openGitModal && (
        <ConfigModal
          open={openGitModal}
          handleClose={handleCloseGitModal}
          template={selectedTemplate}
          createEnv={handleCreateEnvironment}
          templateInputConfigs={templateInputConfigs}
          onModalCreate={handleModalSubmit}
        />
      )}
    </div>
  );
};

export default Home;